import { forwardRef } from 'react';
import { getProvinciaNombre, getCantonNombre, getDistritoNombre } from '../utils/costaRicaLocations';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const formatCurrency = (amount, moneda = 'USD') => {
  const num = parseFloat(amount) || 0;
  if (moneda === 'USD') {
    return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }
  return `₡${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return { dia: '--', mes: '--', año: '--' };
  const date = new Date(dateStr);
  return {
    dia: date.getDate(),
    mes: MESES[date.getMonth()],
    año: date.getFullYear()
  };
};

const InvoiceTemplate = forwardRef(({
  documento,
  emisor,
  receptor,
  lineas = [],
  totales = {},
  esCotizacion = false,
  tipoCambio = 1,
  fullWidth = false
}, ref) => {
  const primaryColor = emisor?.template_color || '#F47920';
  const fecha = formatDate(documento?.fechaEmision || new Date().toISOString());
  const moneda = documento?.codigoMoneda || 'USD';

  // Recalcular totales basado en líneas (considera exoneraciones e impuestos adicionales)
  const calcularTotalesCorrectos = () => {
    let gravado = 0;
    let exento = 0;
    let totalIVA = 0;
    let totalImpuestosAdicionales = 0;
    let totalDescuentos = 0;
    const impuestosAdicionalesMap = {};

    lineas.forEach(linea => {
      const subtotal = parseFloat(linea.cantidad || 0) * parseFloat(linea.precioUnitario || 0);
      totalDescuentos += parseFloat(linea.descuentoMonto || linea.descuento_monto || 0);
      const exoneracion = linea.impuestoExoneracion || linea.impuesto_exoneracion;
      const impuestoMonto = parseFloat(linea.impuestoMonto || linea.impuesto_monto || 0);
      const montoExonerado = exoneracion ? parseFloat(exoneracion.monto || exoneracion.MontoExoneracion || 0) : 0;
      const impuestoNeto = linea.impuestoNeto ?? linea.impuesto_neto ?? Math.max(0, impuestoMonto - montoExonerado);

      if (exoneracion || impuestoMonto === 0) {
        exento += subtotal;
      } else {
        gravado += subtotal;
      }

      totalIVA += impuestoNeto;

      // Collect impuestos adicionales (911, Cruz Roja, etc.)
      const impuestosAd = linea.impuestosAdicionales || linea.impuestos_adicionales || [];
      if (Array.isArray(impuestosAd)) {
        impuestosAd.forEach(imp => {
          const codigo = imp.codigoImpuesto || imp.impuesto_codigo || imp.codigo;
          const porcentaje = parseFloat(imp.porcentaje || imp.tarifa || 0);
          const montoRaw = parseFloat(imp.monto || imp.impuesto_monto || 0) || (subtotal * porcentaje / 100);
          const monto = Math.round(montoRaw * 100) / 100;
          const descripcion = codigo === '07' ? 'Impuesto 911' : codigo === '99' ? 'Cruz Roja' : imp.descripcion || `Impuesto ${codigo}`;

          if (!impuestosAdicionalesMap[codigo]) {
            impuestosAdicionalesMap[codigo] = { descripcion, monto: 0 };
          }
          impuestosAdicionalesMap[codigo].monto += monto;
          totalImpuestosAdicionales += monto;
        });
      }
    });

    const totalImpuesto = totalIVA + totalImpuestosAdicionales;
    // El descuento reduce la venta neta (y por ende el total). El IVA ya viene calculado
    // sobre la base con descuento, así que solo hay que restar el descuento a la venta.
    const totalVenta = gravado + exento - totalDescuentos;
    const totalComprobante = totalVenta + totalImpuesto;

    // Build impuestosDesglose array
    const impuestosDesglose = [];
    if (totalIVA > 0) {
      impuestosDesglose.push({ codigo: '01', descripcion: 'IVA', monto: totalIVA });
    }
    Object.entries(impuestosAdicionalesMap).forEach(([codigo, imp]) => {
      if (imp.monto > 0) {
        impuestosDesglose.push({ codigo, ...imp });
      }
    });

    return {
      totalGravado: gravado,
      totalExento: exento,
      totalDescuentos: totalDescuentos,
      totalImpuesto: totalImpuesto,
      totalOtrosCargos: totalImpuestosAdicionales,    // ← se usa en "Otros" del footer
      totalVenta: totalVenta,
      totalComprobante: totalComprobante,
      impuestosDesglose: impuestosDesglose.length > 0 ? impuestosDesglose : undefined
    };
  };

  const totalesCalculados = lineas.length > 0 ? calcularTotalesCorrectos() : totales;

  // Calcular fecha de vencimiento
  const calcularVencimiento = () => {
    if (!documento?.fechaEmision) return '--/--/----';
    const fechaEmision = new Date(documento.fechaEmision);
    // Prioridad: documento (override) > receptor (default)
    const diasCredito = documento?.diasCredito ?? receptor?.dias_credito ?? 30;
    fechaEmision.setDate(fechaEmision.getDate() + diasCredito);
    return `${fechaEmision.getDate().toString().padStart(2, '0')}/${(fechaEmision.getMonth() + 1).toString().padStart(2, '0')}/${fechaEmision.getFullYear()}`;
  };

  // Términos de pago
  const terminosPago = () => {
    const condicion = documento?.condicionVenta || documento?.condicion_venta;
    if (condicion === '01') return 'Contado';
    const dias = documento?.diasCredito ?? receptor?.dias_credito ?? 30;
    if (dias === 0) return 'Contado';
    return `Neto ${dias} días`;
  };

  // Número de documento
  const getNumeroDocumento = () => {
    if (esCotizacion) {
      return documento?.consecutivo || 'COT-XXXXXX';
    }
    return documento?.consecutivo || '00100001010000000000';
  };

  // Tipo de documento
  const getTipoDocumento = () => {
    if (esCotizacion) return 'Cotización';
    const tipos = {
      '01': 'Factura Electrónica',
      '02': 'Nota de Débito',
      '03': 'Nota de Crédito',
      '04': 'Tiquete Electrónico'
    };
    return tipos[documento?.tipoDocumento] || 'Factura Electrónica';
  };

  // Dirección del emisor
  const getDireccionEmisor = () => {
    const parts = [];
    if (emisor?.otras_senas) parts.push(emisor.otras_senas);
    // Convert codes to names
    const ubicacion = [];
    if (emisor?.distrito && emisor?.canton && emisor?.provincia) {
      ubicacion.push(getDistritoNombre(emisor.provincia, emisor.canton, emisor.distrito));
    }
    if (emisor?.canton && emisor?.provincia) {
      ubicacion.push(getCantonNombre(emisor.provincia, emisor.canton));
    }
    if (emisor?.provincia) {
      ubicacion.push(getProvinciaNombre(emisor.provincia));
    }
    if (ubicacion.length > 0) parts.push(ubicacion.join(', '));
    parts.push('Costa Rica');
    return parts;
  };

  // Dirección del receptor
  const getDireccionReceptor = () => {
    const parts = [];
    if (receptor?.otras_senas) parts.push(receptor.otras_senas);
    // Convert codes to names
    const ubicacion = [];
    if (receptor?.distrito && receptor?.canton && receptor?.provincia) {
      ubicacion.push(getDistritoNombre(receptor.provincia, receptor.canton, receptor.distrito));
    }
    if (receptor?.canton && receptor?.provincia) {
      ubicacion.push(getCantonNombre(receptor.provincia, receptor.canton));
    }
    if (receptor?.provincia) {
      ubicacion.push(getProvinciaNombre(receptor.provincia));
    }
    if (ubicacion.length > 0) parts.push(ubicacion.join(', '));
    parts.push('Costa Rica');
    return parts;
  };

  // Use emisor's terms or empty (no default fake data)
  const terminos = emisor?.terminos_condiciones || '';
  const totalColones = (totalesCalculados?.totalComprobante || totales?.totalComprobante || 0) * tipoCambio;

  return (
    <div ref={ref} className="bg-white factura-hoja" style={{ position: 'relative', fontFamily: 'Arial, sans-serif', fontSize: '10px', lineHeight: '1.3', width: fullWidth ? '100%' : '210mm', minHeight: fullWidth ? 'auto' : '297mm', padding: fullWidth ? '0' : '10mm', boxSizing: 'border-box' }}>
      {/* Watermark */}
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '80px', fontWeight: 'bold', color: primaryColor, opacity: 0.08, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
        !Gracias!
      </div>
      {/* Sello CANCELADO */}
      {(documento?.estadoPago === 'pagado' || documento?.estado_pago === 'pagado' || documento?.condicionVenta === '01' || documento?.condicion_venta === '01') && (
        <div style={{ position: 'absolute', top: '30px', left: '50%', transform: 'translateX(-50%)', fontSize: '22px', fontWeight: 'bold', color: '#16a34a', opacity: 0.5, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 10, letterSpacing: '4px', border: '3px solid #16a34a', padding: '3px 16px', borderRadius: '6px' }}>
          CANCELADO
        </div>
      )}


      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
        {/* Emisor Info */}
        <div style={{ fontSize: '9px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '3px' }}>{emisor?.nombre || 'Empresa S.A.'}</div>
          {getDireccionEmisor().map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          <div>Tel: {emisor?.telefono_codigo || '506'}-{emisor?.telefono_numero || '0000-0000'}</div>
          <div>Cédula Jurídica: {emisor?.identificacion}</div>
          <div>Email: {emisor?.email || 'info@empresa.com'}</div>
        </div>

        {/* Logo */}
        <div style={{ textAlign: 'center' }}>
          {emisor?.logo_url && (
            <img src={emisor.logo_url} alt="Logo" style={{ maxHeight: '80px', maxWidth: '200px', objectFit: 'contain' }} />
          )}
        </div>
      </div>

      {/* Document Header Bar */}
      <div style={{ display: 'flex', marginBottom: '10px', border: '1px solid #000' }}>
        <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #000' }}>
          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
            {getTipoDocumento()} No. {getNumeroDocumento()}
          </div>
          {!esCotizacion && documento?.clave && (
            <div style={{ fontSize: '8px', color: '#666' }}>
              Clave: {documento.clave}
            </div>
          )}
        </div>
        <div style={{ display: 'flex' }}>
          <div style={{ width: '50px', textAlign: 'center', borderRight: '1px solid #000' }}>
            <div style={{ backgroundColor: primaryColor, color: 'white', padding: '2px', fontWeight: 'bold', fontSize: '9px' }}>Día</div>
            <div style={{ padding: '5px', fontWeight: 'bold' }}>{fecha.dia}</div>
          </div>
          <div style={{ width: '60px', textAlign: 'center', borderRight: '1px solid #000' }}>
            <div style={{ backgroundColor: primaryColor, color: 'white', padding: '2px', fontWeight: 'bold', fontSize: '9px' }}>Mes</div>
            <div style={{ padding: '5px', fontWeight: 'bold' }}>{fecha.mes}</div>
          </div>
          <div style={{ width: '50px', textAlign: 'center' }}>
            <div style={{ backgroundColor: primaryColor, color: 'white', padding: '2px', fontWeight: 'bold', fontSize: '9px' }}>Año</div>
            <div style={{ padding: '5px', fontWeight: 'bold' }}>{fecha.año}</div>
          </div>
        </div>
      </div>

      {/* Referencia - Para NC y ND */}
      {documento?.tipoDocumento === '03' && documento?.referencia && (
        <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fef2f2', border: '2px solid #dc2626', borderRadius: '4px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '11px', color: '#dc2626', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '14px' }}>⚠️</span> DOCUMENTO DE REFERENCIA (ANULADO)
          </div>
          <table style={{ width: '100%', fontSize: '9px' }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 'bold', width: '140px', paddingBottom: '3px' }}>Tipo Documento:</td>
                <td style={{ paddingBottom: '3px' }}>
                  {documento.referencia.tipoDoc === '01' ? 'Factura Electrónica' :
                   documento.referencia.tipoDoc === '02' ? 'Nota de Débito' :
                   documento.referencia.tipoDoc === '04' ? 'Tiquete Electrónico' : 'Documento'}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'bold', paddingBottom: '3px' }}>Consecutivo Electrónico:</td>
                <td style={{ paddingBottom: '3px', fontFamily: 'monospace', fontWeight: 'bold', fontSize: '10px' }}>
                  {documento.referencia.consecutivo || '-'}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'bold', paddingBottom: '3px' }}>Clave Numérica:</td>
                <td style={{ paddingBottom: '3px', fontFamily: 'monospace', fontSize: '7px', color: '#666' }}>{documento.referencia.numero}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'bold', paddingBottom: '3px' }}>Fecha Documento:</td>
                <td style={{ paddingBottom: '3px' }}>
                  {documento.referencia.fecha ? new Date(documento.referencia.fecha).toLocaleDateString('es-CR') : '-'}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'bold', paddingBottom: '3px' }}>Motivo:</td>
                <td style={{ paddingBottom: '3px' }}>
                  {documento.referencia.codigo === '01' ? 'Anulación total del documento' :
                   documento.referencia.codigo === '02' ? 'Corrección de texto' :
                   documento.referencia.codigo === '03' ? 'Corrección de monto' :
                   documento.referencia.codigo === '04' ? 'Referencia a otro documento' :
                   documento.referencia.codigo === '05' ? 'Sustituye comprobante provisional' :
                   documento.referencia.razon || 'Anulación'}
                </td>
              </tr>
              {documento.referencia.razon && documento.referencia.codigo !== '01' && (
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Razón:</td>
                  <td>{documento.referencia.razon}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Client Info & Details */}
      <div style={{ display: 'flex', marginBottom: '10px', border: '1px solid #000' }}>
        {/* Client Info */}
        <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #000', lineHeight: '1.3' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '3px', borderBottom: '1px solid #eee', paddingBottom: '2px' }}>
            Información de Cliente:
          </div>
          <div style={{ fontWeight: 'bold' }}>{receptor?.nombre || 'Cliente'}</div>
          <div>Cédula {receptor?.tipo_identificacion === '02' ? 'Jurídica' : 'Física'}: {receptor?.identificacion}</div>
          {getDireccionReceptor().map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          {receptor?.telefono_numero && (
            <div>Tel: +{receptor?.telefono_codigo || '506'} {receptor.telefono_numero}</div>
          )}
          {receptor?.email && (
            <div>Email: {receptor.email}</div>
          )}
        </div>

        {/* Details */}
        <div style={{ width: '230px', padding: '8px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px', borderBottom: '1px solid #eee', paddingBottom: '3px' }}>
            Detalles:
          </div>
          <div style={{ fontSize: '9px', lineHeight: '1.3' }}>
            {documento?.referenciaInterna && (
              <div><span style={{ fontWeight: 'bold' }}>Ref. Interna:</span> {documento.referenciaInterna}</div>
            )}
            <div><span style={{ fontWeight: 'bold' }}>Email FE:</span> <span style={{ fontSize: '8px', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{(receptor?.email_facturacion || receptor?.email || '-').replace(/[;,]\s*/g, '\n')}</span></div>
            <div><span style={{ fontWeight: 'bold' }}>Términos:</span> {terminosPago()}</div>
            <div><span style={{ fontWeight: 'bold' }}>Vencimiento:</span> {calcularVencimiento()}</div>
            <div><span style={{ fontWeight: 'bold' }}>Divisa:</span> {moneda === 'USD' ? 'Dólar Estadounidense' : 'Colón Costarricense'}</div>
            {(() => {
              // Código de actividad económica del CLIENTE (receptor) enviado en la FE. Solo el código.
              const cod = documento?.codigoActividadReceptor
                || String(receptor?.actividad_economica || receptor?.actividadEconomica || '').split(/[\s—-]/)[0].trim();
              return cod ? <div><span style={{ fontWeight: 'bold' }}>Actividad Económica:</span> {cod}</div> : null;
            })()}
            {documento?.ordenCompra && (
              <div><span style={{ fontWeight: 'bold' }}>Referencia:</span> {documento.ordenCompra}</div>
            )}
          </div>
        </div>
      </div>

      {/* Items Table */}
      {(() => { const hayDescuento = lineas.some(l => parseFloat(l.descuentoMonto || l.descuento_monto || 0) > 0); return (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
        <thead>
          <tr style={{ backgroundColor: primaryColor, color: 'white' }}>
            <th style={{ padding: '5px', textAlign: 'left', border: '1px solid #000', width: '80px' }}>Código</th>
            <th style={{ padding: '5px', textAlign: 'left', border: '1px solid #000' }}>Descripción</th>
            <th style={{ padding: '5px', textAlign: 'center', border: '1px solid #000', width: '50px' }}>Cantidad</th>
            <th style={{ padding: '5px', textAlign: 'right', border: '1px solid #000', width: '70px' }}>Precio Unit.</th>
            {hayDescuento && <th style={{ padding: '5px', textAlign: 'right', border: '1px solid #000', width: '70px' }}>Descuento</th>}
            <th style={{ padding: '5px', textAlign: 'right', border: '1px solid #000', width: '60px' }}>IVA</th>
            <th style={{ padding: '5px', textAlign: 'right', border: '1px solid #000', width: '70px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lineas.length > 0 ? lineas.map((linea, index) => {
            const exoneracion = linea.impuestoExoneracion || linea.impuesto_exoneracion;

            // Calcular IVA neto: si hay exoneración, restar el monto exonerado
            const impuestoMonto = parseFloat(linea.impuestoMonto || linea.impuesto_monto || 0);
            const montoExonerado = exoneracion ? parseFloat(exoneracion.monto || exoneracion.MontoExoneracion || 0) : 0;
            const impuestoNeto = linea.impuestoNeto ?? linea.impuesto_neto ?? Math.max(0, impuestoMonto - montoExonerado);

            // Total línea = subtotal + IVA neto
            const subtotal = parseFloat(linea.cantidad || 0) * parseFloat(linea.precioUnitario || 0);
            const totalLinea = linea.montoTotalLinea || linea.monto_total_linea || (subtotal + impuestoNeto);

            return (
            <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '5px', border: '1px solid #ddd', verticalAlign: 'top' }}>
                {linea.sku || linea.codigoComercial || linea.codigo_comercial || '-'}
              </td>
              <td style={{ padding: '6px 5px', border: '1px solid #ddd', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>
                {linea.detalle}
                {linea.nota && (
                  <div style={{ fontSize: '8px', color: '#666', marginTop: '3px' }}>{linea.nota}</div>
                )}
                {exoneracion && (() => {
                  // Compacto: 1 sola línea, ancho ajustado al contenido, sin caja gruesa.
                  // Antes ocupaba toda la fila con padding 3-5px → desperdiciaba espacio vertical.
                  const fecha = exoneracion.fechaEmision || exoneracion.FechaEmision;
                  const fechaStr = fecha ? (() => {
                    const d = new Date(fecha);
                    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
                  })() : '';
                  return (
                    <div style={{ fontSize: '7px', color: '#166534', marginTop: '2px', padding: '1px 4px', backgroundColor: '#f0fdf4', borderRadius: '2px', lineHeight: '1.2', display: 'inline-block' }}>
                      <strong>Exo {exoneracion.porcentaje || exoneracion.PorcentajeExoneracion}%</strong> ({formatCurrency(montoExonerado, moneda)}) · {exoneracion.tipoDocumentoDesc || exoneracion.TipoDocumentoDesc || ''} · {exoneracion.numeroDocumento || exoneracion.NumeroDocumento}{fechaStr ? ` · ${fechaStr}` : ''}
                    </div>
                  );
                })()}
              </td>
              <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'center' }}>
                {parseFloat(linea.cantidad || 0).toFixed(2)}
              </td>
              <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right' }}>
                {formatCurrency(linea.precioUnitario, moneda)}
              </td>
              {hayDescuento && (
                <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right', color: '#16a34a' }}>
                  {parseFloat(linea.descuentoMonto || linea.descuento_monto || 0) > 0 ? formatCurrency(linea.descuentoMonto || linea.descuento_monto, moneda) : '-'}
                </td>
              )}
              <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right', fontSize: '9px' }}>
                {formatCurrency(impuestoNeto, moneda)}
              </td>
              <td style={{ padding: '5px', border: '1px solid #ddd', textAlign: 'right' }}>
                {formatCurrency(totalLinea, moneda)}
              </td>
            </tr>
          )}) : (
            <tr>
              <td colSpan={hayDescuento ? 7 : 6} style={{ padding: '20px', textAlign: 'center', color: '#999', border: '1px solid #ddd' }}>
                No hay líneas de detalle
              </td>
            </tr>
          )}
          {(documento?.periodoCobroTexto || documento?.periodo_cobro_texto) && (
            <tr>
              <td colSpan={hayDescuento ? 7 : 6} style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: '10px', color: '#1e40af', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                {documento.periodoCobroTexto || documento.periodo_cobro_texto}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      ); })()}

      {/* Comentarios y Observaciones — `\t` (tab) en notas desalinea el render por el
          tab-stop default; reemplazamos por espacio simple. */}
      {documento?.notas && (
        <div style={{ marginBottom: '10px', padding: '8px 8px 8px 12px', borderLeft: `3px solid ${primaryColor}`, backgroundColor: '#fafafa', borderRadius: '0 3px 3px 0' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '9px', color: '#333' }}>
            Comentarios y Observaciones:
          </div>
          <div style={{ fontSize: '9px', color: primaryColor, whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
            {String(documento.notas).replace(/\t+/g, ' ')}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', marginTop: 'auto', border: '1px solid #000' }}>
        {/* Términos y Condiciones */}
        <div style={{ flex: 1, padding: '8px', borderRight: '1px solid #000', fontSize: '8px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Términos y Condiciones:</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{terminos}</div>
          {moneda === 'USD' && tipoCambio > 1 && (
            <div style={{ marginTop: '8px', paddingTop: '5px', borderTop: '1px dashed #ccc', fontWeight: 'bold', fontSize: '8px' }}>
              Total Facturado en Colones: ₡{totalColones.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} - Tipo de Cambio: ₡{Number(tipoCambio).toFixed(2)}
            </div>
          )}
        </div>

        {/* Recibido Por */}
        <div style={{ width: '100px', padding: '8px', borderRight: '1px solid #000', textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '30px' }}>Recibido Por:</div>
          <div style={{ borderTop: '1px solid #000', paddingTop: '5px', fontSize: '8px' }}>(Firma)</div>
        </div>

        {/* Totales */}
        <div style={{ minWidth: '200px', padding: '0' }}>
          <table style={{ width: '100%', fontSize: '9px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>SubTotal Gravado:</td>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(totalesCalculados.totalGravado || 0, moneda)}</td>
              </tr>
              {(() => {
                const desc = parseFloat(totalesCalculados?.totalDescuentos ?? totales?.totalDescuentos ?? 0) || 0;
                const st = { padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee', ...(desc > 0 ? { color: '#16a34a', fontWeight: 'bold' } : {}) };
                return (
                  <tr>
                    <td style={st}>Descuento Gravado:</td>
                    <td style={st}>{formatCurrency(desc, moneda)}</td>
                  </tr>
                );
              })()}
              <tr>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>SubTotal Exento:</td>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(totalesCalculados.totalExento || 0, moneda)}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>Descuento Exento:</td>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(0, moneda)}</td>
              </tr>
              {(totalesCalculados?.impuestosDesglose || totales?.impuestosDesglose)?.length > 1 ? (
                (totalesCalculados?.impuestosDesglose || totales?.impuestosDesglose).map((imp, i) => (
                  <tr key={i}>
                    <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{imp.descripcion}:</td>
                    <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(imp.monto, moneda)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>IVA:</td>
                  <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(totalesCalculados.totalImpuesto || 0, moneda)}</td>
                </tr>
              )}
              <tr>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>Total:</td>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(totalesCalculados.totalVenta || 0, moneda)}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>Otros:</td>
                <td style={{ padding: '3px 5px', textAlign: 'right', borderBottom: '1px solid #eee' }}>{formatCurrency(totalesCalculados?.totalOtrosCargos || totales?.totalOtrosCargos || 0, moneda)}</td>
              </tr>
              <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <td style={{ padding: '5px', textAlign: 'right' }}>{
                  // NC (03) y ND (02) NO se pagan — son ajustes contables → "Monto Total".
                  // FE (01), TE (04), FEC (08), FEE (09) sí se pagan → "Total a Pagar".
                  ['03', '02'].includes(documento?.tipoDocumento || documento?.tipo_documento) ? 'Monto Total:' : 'Total a Pagar:'
                }</td>
                <td style={{ padding: '5px', textAlign: 'right' }}>{formatCurrency(totalesCalculados.totalComprobante || 0, moneda)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Info */}
      <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', fontSize: '8px', borderTop: '1px solid #ddd', paddingTop: '5px' }}>
        <div>Página 1 de 1</div>
      </div>

      {/* Legal Footer */}
      <div style={{ marginTop: '5px', fontSize: '7px', color: '#666', textAlign: 'center' }}>
        {!esCotizacion && (
          <>
            Emitida conforme lo establecido en la resolución de Facturación Electrónica No. DGT-RES-0027-2024 del 19-Nov-2024. de la D.G.T.D.
            <br />
            Versión 4.4.
          </>
        )}
        <div style={{ marginTop: '3px' }}>
          {esCotizacion ? 'Cotización' : getTipoDocumento()} emitida el: {new Date().toLocaleDateString('es-CR')} {new Date().toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
});

InvoiceTemplate.displayName = 'InvoiceTemplate';

export default InvoiceTemplate;
